import { useState, useEffect, useRef, useCallback } from "react";
import { agentApi, trackingApi, alertApi } from "../services/api.service";
import { normalizeLiveAgent, normalizeUserAgent } from "../utils/normalizers";
import { POLL_INTERVAL } from "../config/api.config";

export const AGENTS_REFRESH_EVENT = "at_agents_refresh";

function isAccountOn(u) {
  return !u?.isDeleted && u?.status !== "off";
}

function fromUserList(raw) {
  return raw.filter(isAccountOn).map((u, i) => normalizeUserAgent(u, i));
}

export function useAgents() {
  const [agents,    setAgents]    = useState([]);
  const [alerts,    setAlerts]    = useState([]);
  const [zones,     setZones]     = useState([]);
  const [loading,   setLoading]   = useState(false);
  const pollRef = useRef(null);

  const loadAgents = useCallback(async ({ showLoading = false } = {}) => {
    if (showLoading) setLoading(true);
    try {
      const res = await agentApi.list();
      const raw = Array.isArray(res.data?.data) ? res.data.data
        : Array.isArray(res.data) ? res.data : [];
      const users = fromUserList(raw);
      setAgents(users);
    } catch (e) {
      console.info("[AgentTracker] user/list failed:", e.message);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  // ── Load agent list on mount ──────────────────────────────────
  useEffect(() => {
    loadAgents({ showLoading: true });
  }, [loadAgents]);

  useEffect(() => {
    const onRefresh = () => { loadAgents(); };
    window.addEventListener(AGENTS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(AGENTS_REFRESH_EVENT, onRefresh);
  }, [loadAgents]);

  // ── Poll live positions ───────────────────────────────────────
  const fetchLive = useCallback(async () => {
    try {
      const res = await trackingApi.allLive();
      const raw = Array.isArray(res.data?.data) ? res.data.data
        : Array.isArray(res.data) ? res.data : [];
      setAgents(prev => {
        const liveMap = {};
        raw.forEach((a, i) => { liveMap[a.id] = normalizeLiveAgent(a, i); });
        const updated = prev
          .filter(agent => agent.accountStatus !== "off")
          .map(agent => {
            const live = liveMap[agent.id];
            if (!live) return agent;
            return {
              ...agent,
              lat: live.lat,
              lng: live.lng,
              speed: live.speed,
              heading: live.heading,
              status: live.status,
              address: live.address,
              isReporting: live.isReporting,
              lastSeen: live.lastSeen,
              accountStatus: agent.accountStatus || "on",
            };
          });
        raw.forEach((a, i) => {
          if (!prev.find(p => p.id === a.id)) {
            updated.push({ ...normalizeLiveAgent(a, prev.length + i), accountStatus: "on" });
          }
        });
        return updated.filter(a => a.accountStatus !== "off");
      });
    } catch {
    }
  }, []);

  useEffect(() => {
    fetchLive();
    pollRef.current = setInterval(fetchLive, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchLive]);

  // ── CREATE agent — POST /users ────────────────────────────────
  const createAgent = useCallback(async (data) => {
    const res = await agentApi.create(data);
    if (!res.data?.status) throw new Error(res.data?.message || "Create failed");
    // Refresh full list to get new agent with server-assigned ID
    try {
      const listRes = await agentApi.list();
      const raw = Array.isArray(listRes.data?.data) ? listRes.data.data : [];
      setAgents(fromUserList(raw));
    } catch { /* silent — agent still created */ }
    return { success: true, message: res.data.message };
  }, [agents.length]);

  // ── UPDATE profile — PATCH /users/:id ────────────────────────
  const updateAgent = useCallback(async (id, data) => {
    // Optimistic update
    setAgents(prev => prev.map(a => a.id === id
      ? { ...a, name: data.fullName, fullName: data.fullName, city: data.city||"",
          address: data.address||"", pincode: data.pincode||"", alternateMobile: data.alternateMobile||"" }
      : a));
      const res = await agentApi.update(id, data);
      if (!res.data?.status) throw new Error(res.data?.message || "Update failed");
    return { success: true };
  }, []);

  // ── UPDATE password — PATCH /users/:id/password ───────────────
  const updatePassword = useCallback(async (id, password) => {
      const res = await agentApi.updatePassword(id, password);
      if (!res.data?.status) throw new Error(res.data?.message || "Password update failed");
    return { success: true };
  }, []);

  // ── UPDATE status — PATCH /users/:id/status ───────────────────
  const updateStatus = useCallback(async (id, status) => {
    // status param = "on" | "off" — off agents belong only on the Users page
    setAgents(prev =>
      status === "off"
        ? prev.filter(a => a.id !== id)
        : prev.map(a => a.id === id ? { ...a, accountStatus: "on" } : a)
    );
    const res = await agentApi.updateStatus(id, status);
    if (!res.data?.status) throw new Error(res.data?.message || "Status update failed");
    if (status === "on") await loadAgents();
    return { success: true };
  }, [loadAgents]);

  // ── UPDATE location permission — PATCH /users/:id/location-permission ─
  const updateLocationPerm = useCallback(async (id, allowAnyLocation) => {
    // allowAnyLocation: 0 | 1
    setAgents(prev => prev.map(a => a.id === id ? { ...a, allowAnyLocation } : a));
      const res = await agentApi.updateLocationPerm(id, allowAnyLocation);
      if (!res.data?.status) throw new Error(res.data?.message || "Permission update failed");
    return { success: true };
  }, []);

  // ── Alerts ────────────────────────────────────────────────────
  const sendAlert = useCallback(async ({ agentId, type, title, message, priority }) => {
    const agent = agents.find(a => a.id === agentId);
    const al = {
      id: `al_${Date.now()}`, type, title, message, priority, agentId,
      agentName: agentId === "all" ? "All Agents" : (agent?.name || String(agentId)),
      status: "sent", sentAt: new Date().toISOString(), ackAt: null,
    };
    setAlerts(prev => [al, ...prev]);
    try { await alertApi.send({ agentId, type, title, message, priority }); }
    catch { /* optimistic */ }
    return al;
  }, [agents]);

  const cancelAlert = useCallback(async id => {
    setAlerts(prev => prev.filter(a => a.id !== id));
    try { await alertApi.cancel(id); } catch { /* silent */ }
  }, []);

  const activeAgents  = agents.filter(a => a.status === "active").length;
  const idleAgents    = agents.filter(a => a.status === "idle").length;
  const offlineAgents = agents.filter(a => a.status === "offline").length;
  const sosAgents     = agents.filter(a => a.status === "sos");
  const activeAlerts  = alerts.filter(a => a.status === "active" || a.status === "sent").length;

  return {
    agents, alerts, zones, loading,
    activeAgents, idleAgents, offlineAgents, sosAgents, activeAlerts,
    createAgent, updateAgent, updatePassword, updateStatus, updateLocationPerm,
    sendAlert, cancelAlert,
    refetchLive: fetchLive,
  };
}
